import { XCircle, CreditCard, Wallet, AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

interface PaymentFailureProps {
  errorReason: string;
  onRetry: () => void;
  onChangePaymentMethod: () => void;
  onSwitchToPayLater: () => void;
  onCancel: () => void;
}

export function PaymentFailure({ 
  errorReason, 
  onRetry, 
  onChangePaymentMethod,
  onSwitchToPayLater,
  onCancel 
}: PaymentFailureProps) {
  const getErrorDetails = () => {
    const errors: Record<string, { title: string; description: string; suggestions: string[] }> = {
      'card_declined': {
        title: 'Card Declined',
        description: 'Your card was declined by your bank.',
        suggestions: [
          'Check if you have sufficient funds',
          'Verify your card is activated for online payments',
          'Try a different card',
          'Contact your bank for more information'
        ]
      },
      'insufficient_funds': {
        title: 'Insufficient Funds',
        description: 'Your card does not have enough balance for this transaction.',
        suggestions: [
          'Try a different payment method',
          'Add funds to your account',
          'Use a different card'
        ]
      },
      'network_error': {
        title: 'Network Issue',
        description: 'We couldn\'t connect to process your payment.',
        suggestions: [
          'Check your internet connection',
          'Try again in a few moments',
          'If the problem persists, contact support'
        ]
      },
      'expired_card': {
        title: 'Card Expired',
        description: 'The card you\'re trying to use has expired.',
        suggestions: [
          'Use a different card',
          'Check if your bank issued a new card'
        ]
      },
      'invalid_cvv': {
        title: 'Invalid CVV',
        description: 'The security code (CVV) you entered is incorrect.',
        suggestions: [
          'Check the 3-digit code on the back of your card',
          'Try entering it again carefully'
        ]
      },
      'default': {
        title: 'Payment Failed',
        description: 'We couldn\'t process your payment.',
        suggestions: [
          'Try again with the same card',
          'Use a different payment method',
          'Contact support if the issue continues'
        ]
      }
    };

    return errors[errorReason] || errors['default'];
  };

  const error = getErrorDetails();

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F7F8FA' }}>
      <div className="w-full max-w-2xl">
        <div className="p-8 text-center" style={{ backgroundColor: '#FFFFFF', borderRadius: '24px', border: '1px solid #E2DFEA', boxShadow: '0 12px 32px rgba(89, 75, 255, 0.08)' }}>
          {/* Error Icon */}
          <div className="mb-6 flex justify-center">
            <div 
              className="w-24 h-24 flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                borderRadius: '50%'
              }}
            >
              <XCircle className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Error Message */}
          <h2 style={{ fontSize: '2rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B', marginBottom: '0.5rem' }}>
            {error.title}
          </h2>
          <p style={{ fontSize: '1.125rem', fontFamily: 'Inter, sans-serif', color: '#464452', marginBottom: '2rem' }}>
            {error.description}
          </p>

          {/* Error Details */}
          <div className="mb-6 p-6 text-left" style={{ backgroundColor: '#FEE2E2', borderRadius: '16px', border: '1px solid #EF4444' }}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#991B1B' }} />
              <div>
                <h3 style={{ fontSize: '1rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#991B1B', marginBottom: '0.5rem' }}>
                  What you can try:
                </h3>
                <ul className="space-y-2">
                  {error.suggestions.map((suggestion, index) => (
                    <li 
                      key={index}
                      style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#991B1B', paddingLeft: '1rem' }}
                      className="relative before:content-['•'] before:absolute before:left-0"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* No Charge Notice */}
          <div className="mb-6 p-4" style={{ backgroundColor: '#DBEAFE', borderRadius: '12px', border: '1px solid #3B82F6' }}>
            <p style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF' }}>
              <strong>Don't worry!</strong> You were not charged. Your booking is still pending and you can complete the payment now.
            </p>
          </div>

          {/* Action Options */}
          <div className="space-y-3">
            {/* Retry Payment */}
            <button
              onClick={onRetry}
              className="w-full flex items-center justify-center gap-2 py-4 text-white transition-all"
              style={{
                background: 'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
                borderRadius: '16px',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: '1.125rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>

            {/* Change Card */}
            <button
              onClick={onChangePaymentMethod}
              className="w-full flex items-center justify-center gap-2 py-4 transition-all"
              style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #594BFF',
                color: '#594BFF',
                borderRadius: '16px',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: '1.125rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#F0EEFF';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
              }}
            >
              <CreditCard className="w-5 h-5" />
              Use Different Card
            </button>

            {/* Pay Later Option */}
            <button
              onClick={onSwitchToPayLater}
              className="w-full flex items-center justify-center gap-2 py-4 transition-all"
              style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #E2DFEA',
                color: '#2D1F7B',
                borderRadius: '16px',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: '1.125rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#594BFF';
                e.currentTarget.style.backgroundColor = '#F0EEFF';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E2DFEA';
                e.currentTarget.style.backgroundColor = '#FFFFFF';
              }}
            >
              <Wallet className="w-5 h-5" />
              Switch to Pay at Appointment
            </button>

            {/* Cancel */}
            <button
              onClick={onCancel}
              className="w-full py-3 transition-colors flex items-center justify-center gap-2"
              style={{
                color: '#464452',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#2D1F7B'}
              onMouseOut={(e) => e.currentTarget.style.color = '#464452'}
            >
              <ArrowLeft className="w-4 h-4" />
              Cancel Booking
            </button>
          </div>

          {/* Support Notice */}
          <div className="mt-6 pt-6" style={{ borderTop: '1px solid #E2DFEA' }}>
            <p style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#464452' }}>
              Need help? Contact our <a href="#" style={{ color: '#594BFF', fontWeight: 600 }} className="hover:underline">support team</a> and we'll assist you with your booking.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
